"use client";

import { useEffect } from "react";

import type { ReactElement } from "react";
import type { AdvertoryDeviceType, AdvertoryTargetId } from "./types";

import { useAdvertory } from "./AdvertoryContext";
import { useAdvertoryPage } from "./AdvertoryPage";

const isWidePlacement = (targetId: AdvertoryTargetId) =>
  targetId === "toppanorama" ||
  targetId === "integration_1" ||
  targetId === "integration_3" ||
  targetId.startsWith("panorama") ||
  targetId.startsWith("modul");

const AdvertorySlot = ({
  device = "all",
  targetId,
  nativeLayout,
  reserveSpace = false,
  showAdSkeleton = false,
}: {
  device?: AdvertoryDeviceType | "all";
  targetId: AdvertoryTargetId;
  nativeLayout?: "small" | "large";
  reserveSpace?: boolean;
  showAdSkeleton?: boolean;
}): ReactElement | null => {
  const {
    deviceType,
    enabled,
    activePageId,
    activePageMode,
    backgroundPageId,
    isAdvertoryPage,
    refreshPlacements,
  } = useAdvertory();
  const { id, active, mode, creativeKey } = useAdvertoryPage();
  const widePlacement = isWidePlacement(targetId);
  const shouldRender =
    isAdvertoryPage &&
    active &&
    enabled &&
    (device === "all" || device === deviceType);
  // Keep SDK-owned DOM (including live iframes) connected behind the modal.
  const shouldPreserve =
    enabled &&
    activePageMode === "modal" &&
    mode === "page" &&
    id === backgroundPageId &&
    (device === "all" || device === deviceType);
  const shouldReserve = reserveSpace && widePlacement && enabled;
  const shouldShowAdSkeleton =
    showAdSkeleton && enabled && (active || activePageId === null);

  useEffect(() => {
    if (shouldRender) refreshPlacements();
  }, [refreshPlacements, shouldRender, targetId]);

  if (
    !shouldRender &&
    !shouldPreserve &&
    !shouldReserve &&
    !shouldShowAdSkeleton
  )
    return null;

  const nativePlacement = nativeLayout !== undefined;
  const className = [
    widePlacement && "advertory-omni-panorama",
    !nativePlacement && "advertory-omni-ad-slot",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      // The SDK owns both the container and its collapsed/fullscreen wrapper.
      key={creativeKey}
      className={`advertory-omni-ad-slot-wrapper${widePlacement ? " advertory-omni-wide-slot-wrapper" : ""}${shouldReserve ? " advertory-omni-panorama-reserved-wrapper" : ""}${shouldShowAdSkeleton ? " advertory-omni-article-ad-skeleton-wrapper" : ""}`}
      data-advertory-slot={targetId}
      data-advertory-device={device}
    >
      {(shouldRender || shouldPreserve) && (
        <div
          id={targetId}
          data-disabled={!shouldRender ? "true" : undefined}
          className={className || undefined}
          data-native-layout={nativeLayout}
        />
      )}
      {shouldShowAdSkeleton && (
        <div className="advertory-omni-article-ad-skeleton" aria-hidden="true">
          <div className="advertory-omni-article-ad-skeleton-label">ANNONS</div>
          <div className="advertory-omni-article-ad-skeleton-creative" />
        </div>
      )}
    </div>
  );
};

export { AdvertorySlot };
